/** A visual-only tree: never mount Settings effects on a neighboring display. */
export type SettingsPreviewNode =
  | string
  | {
      tag: string;
      attributes: Record<string, string>;
      style: Record<string, string>;
      children: SettingsPreviewNode[];
      scrollTop?: number;
      scrollLeft?: number;
    };
export interface SettingsFramePreview {
  root: SettingsPreviewNode;
}

const TAGS = new Set(
  "div span p strong em b i small label nav section header footer main article aside h1 h2 h3 h4 h5 h6 ul ol li button input textarea select option optgroup img svg path circle ellipse rect line polyline polygon g defs linearGradient radialGradient stop clipPath br hr table thead tbody tr th td pre code a"
    .toLowerCase()
    .split(" "),
);
const ATTRIBUTES = new Set(
  "class type placeholder title disabled readonly rows cols size min max step width height viewBox d fill stroke stroke-width stroke-linecap stroke-linejoin cx cy r rx ry x y x1 y1 x2 y2 points opacity fill-opacity stroke-opacity offset stop-color stop-opacity preserveAspectRatio data-state data-disabled data-denker-surface-role"
    .toLowerCase()
    .split(" "),
);
const SVG_TAGS = new Set(
  "svg path circle ellipse rect line polyline polygon g defs lineargradient radialgradient stop clippath".split(
    " ",
  ),
);
const SVG_NAMES: Record<string, string> = {
  lineargradient: "linearGradient",
  radialgradient: "radialGradient",
  clippath: "clipPath",
};
const ATTRIBUTE_NAMES: Record<string, string> = {
  viewbox: "viewBox",
  preserveaspectratio: "preserveAspectRatio",
};

function safeStyle(value: string): boolean {
  return !/url\s*\(|expression\s*\(|@import/i.test(value);
}
function safeImage(value: string): boolean {
  return /^(https?:|blob:|data:image\/(?:png|jpeg|gif|webp|avif|svg\+xml)[;,])/i.test(
    value,
  );
}

export function captureSettingsFramePreview(
  element: HTMLElement,
): SettingsFramePreview {
  const capture = (node: Node): SettingsPreviewNode | null => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (!(node instanceof Element)) return null;
    const tag = node.tagName.toLowerCase();
    if (!TAGS.has(tag)) return null;
    const attributes: Record<string, string> = {};
    for (const attribute of Array.from(node.attributes)) {
      const name = attribute.name.toLowerCase();
      if (ATTRIBUTES.has(name) && safeStyle(attribute.value))
        attributes[name] = attribute.value;
    }
    if (
      node instanceof HTMLImageElement &&
      safeImage(node.currentSrc || node.src)
    )
      attributes.src = node.currentSrc || node.src;
    const style: Record<string, string> = {};
    const inlineStyle = (node as HTMLElement | SVGElement).style;
    if (inlineStyle)
      for (const property of Array.from(inlineStyle)) {
        const value = inlineStyle.getPropertyValue(property);
        if (safeStyle(value)) style[property] = value;
      }
    if (node instanceof HTMLInputElement) {
      attributes.value =
        node.type === "password" ? "•".repeat(node.value.length) : node.value;
      if (node.checked) attributes.checked = "";
    }
    if (node instanceof HTMLOptionElement && node.selected)
      attributes.selected = "";
    const children =
      node instanceof HTMLTextAreaElement
        ? [node.value]
        : Array.from(node.childNodes)
            .map(capture)
            .filter((child): child is SettingsPreviewNode => child !== null);
    return {
      tag,
      attributes,
      style,
      children,
      ...(node.scrollTop ? { scrollTop: node.scrollTop } : {}),
      ...(node.scrollLeft ? { scrollLeft: node.scrollLeft } : {}),
    };
  };
  return { root: capture(element) ?? "" };
}

/** Re-check the visual schema at the transport boundary; no HTML parsing. */
export function mountSettingsFramePreview(
  host: HTMLElement,
  preview: SettingsFramePreview,
): void {
  const scroll: Array<{ element: Element; top: number; left: number }> = [];
  const build = (node: SettingsPreviewNode): Node => {
    if (typeof node === "string") return document.createTextNode(node);
    if (!node || !TAGS.has(node.tag)) return document.createTextNode("");
    const element = SVG_TAGS.has(node.tag)
      ? document.createElementNS(
          "http://www.w3.org/2000/svg",
          SVG_NAMES[node.tag] ?? node.tag,
        )
      : document.createElement(node.tag);
    for (const [name, value] of Object.entries(node.attributes)) {
      if (ATTRIBUTES.has(name) && safeStyle(value))
        element.setAttribute(ATTRIBUTE_NAMES[name] ?? name, value);
      else if (name === "src" && node.tag === "img" && safeImage(value))
        element.setAttribute(name, value);
      else if (
        ["value", "checked", "selected"].includes(name) &&
        ["input", "option"].includes(node.tag)
      )
        element.setAttribute(name, value);
    }
    for (const [property, value] of Object.entries(node.style))
      if (safeStyle(value))
        (element as HTMLElement | SVGElement).style.setProperty(
          property,
          value,
        );
    element.append(...node.children.map(build));
    scroll.push({
      element,
      top: node.scrollTop ?? 0,
      left: node.scrollLeft ?? 0,
    });
    return element;
  };
  const root = build(preview.root);
  if (root instanceof HTMLElement) {
    Object.assign(root.style, {
      position: "relative",
      transform: "none",
      left: "0",
      top: "0",
      width: "100%",
      height: "100%",
    });
  }
  host.replaceChildren(root);
  for (const entry of scroll) {
    entry.element.scrollTop = entry.top;
    entry.element.scrollLeft = entry.left;
  }
}
